import React, { useEffect, useState, useCallback, useRef } from 'react'
import adminApi from './api'
import { API_URL } from '../api/base'



// Custom hook for counter animation with ~10 steps
const useCountUp = (end: number, duration: number = 1000) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (end === 0) {
      setCount(0)
      return
    }

    const steps = 20 // Number of animation steps
    const stepDuration = duration / steps
    let currentStep = 0

    const interval = setInterval(() => {
      currentStep++
      const progress = currentStep / steps

      // Easing function for smooth acceleration
      const easeOutQuad = (t: number) => t * (2 - t)
      const currentCount = Math.floor(end * easeOutQuad(progress))

      setCount(currentCount)

      if (currentStep >= steps) {
        setCount(end)
        clearInterval(interval)
      }
    }, stepDuration)

    return () => clearInterval(interval)
  }, [end, duration])

  return count
}

interface Donation {
  order_id: string
  gateway: string
  status: string
  amount_kgs: number
  trees_count: number
  created_at: string
  paid_at: string | null
  certificate_number: string | null
  certificate_sent_at: string | null
  ticket_number: string | null
  participants_count: number | null
  comment: string | null
  is_partner: boolean
  first_name: string
  last_name: string
  email: string
  phone: string
  instagram_url: string | null
  organization_name: string | null
  event_id: number | null
  region_id: number | null
  region_name: { ru?: string; ky?: string; en?: string } | null
  region_code: string | null
  company_id: number | null
  company_name: string | null
  company_logo_url: string | null
}

interface EventOption {
  id: number
  name: string
  isActiveDefault: boolean
}

interface RegionOption {
  id: number
  code: string
  name: { ru?: string; ky?: string; en?: string }
}

interface CompanyOption {
  id: number
  name: string
  logo_url: string | null
}

// 1 дерево = 249 сом (публичная константа с сайта)
const TREE_PRICE = 249

/**
 * Единый label для колонки «Донор»:
 *   partner + company → название компании
 *   partner без company → ФИО (партнёр-физлицо)
 *   client → ФИО
 * Всегда возвращаем что-то содержательное вместо «—», когда есть хоть какие-то данные.
 */
const donorLabel = (d: { is_partner: boolean; company_name?: string | null; organization_name?: string | null; first_name?: string; last_name?: string }): string => {
  const fio = `${d.first_name || ''} ${d.last_name || ''}`.trim()
  if (d.is_partner) {
    return d.company_name?.trim() || d.organization_name?.trim() || fio || '—'
  }
  return fio || '—'
}

interface Statistics {
  totalPayments: number
  treesPlanted: number
  totalAmount: number
  uniqueDonors: number
}

export const DonationsAdmin: React.FC = () => {
  const [donations, setDonations] = useState<Donation[]>([])
  const [statistics, setStatistics] = useState<Statistics>({
    totalPayments: 0,
    treesPlanted: 0,
    totalAmount: 0,
    uniqueDonors: 0
  })
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [userRole, setUserRole] = useState<string>('')

  // Animated counters for statistics
  const animatedTotalPayments = useCountUp(statistics.totalPayments)
  const animatedTreesPlanted = useCountUp(statistics.treesPlanted)
  const animatedTotalAmount = useCountUp(statistics.totalAmount)
  const animatedUniqueDonors = useCountUp(statistics.uniqueDonors)
  const [limit, setLimit] = useState(50)
  const [offset, setOffset] = useState(0)
  const [filter, setFilter] = useState({
    status: '',
    gateway: '',
    search: '',
    dateFrom: '',
    dateTo: '',
    treesFrom: '',
    treesTo: '',
    isPartner: '',
    recordType: '', // 'certificate' | 'ticket' | ''
    regionId: '' // 'null' | '<id>' | ''
  })
  const [regions, setRegions] = useState<RegionOption[]>([])
  const [sortBy, setSortBy] = useState<'trees' | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showFilters, setShowFilters] = useState(false)
  const [showTreesModal, setShowTreesModal] = useState(false)
  const [tempTreesFrom, setTempTreesFrom] = useState('')
  const [tempTreesTo, setTempTreesTo] = useState('')
  const [showDateModal, setShowDateModal] = useState(false)
  const [tempDateFrom, setTempDateFrom] = useState('')
  const [tempDateTo, setTempDateTo] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [selectedDonation, setSelectedDonation] = useState<Donation | null>(null)
  const [editedIsPartner, setEditedIsPartner] = useState(false)
  const [editedCompanyId, setEditedCompanyId] = useState<string>('')
  const [editedCompanySearch, setEditedCompanySearch] = useState('')
  const [editedCompanyDropdownOpen, setEditedCompanyDropdownOpen] = useState(false)
  const editedCompanyDropdownRef = useRef<HTMLDivElement | null>(null)
  const [savingPartner, setSavingPartner] = useState(false)

  // Закрытие selectPartner в модалке деталей
  useEffect(() => {
    if (!editedCompanyDropdownOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (editedCompanyDropdownRef.current && !editedCompanyDropdownRef.current.contains(e.target as Node)) {
        setEditedCompanyDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [editedCompanyDropdownOpen])
  const [isEditing, setIsEditing] = useState(false)
  const [editedOrganizationName, setEditedOrganizationName] = useState('')
  const [editedFirstName, setEditedFirstName] = useState('')
  const [editedLastName, setEditedLastName] = useState('')
  const [editedInstagramUrl, setEditedInstagramUrl] = useState('')
  const [isEditingInstagram, setIsEditingInstagram] = useState(false)
  const [savingInstagram, setSavingInstagram] = useState(false)
  const [certificatePopup, setCertificatePopup] = useState<string | null>(null)
  const [popupPosition, setPopupPosition] = useState<'top' | 'bottom'>('bottom')
  const popupTriggerRef = useRef<HTMLSpanElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    instagram: '',
    gateway: 'bank_transfer',
    treesCount: '',
    amount: '',
    barterComment: '',
    isPartner: false,
    organizationName: '',
    eventId: '',
    companyId: '' // FK на companies для партнёрского режима
  })
  // paymentMode удалён — используем formData.isPartner напрямую
  const [companies, setCompanies] = useState<CompanyOption[]>([])
  const [companySearch, setCompanySearch] = useState('')
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false)
  const companyDropdownRef = useRef<HTMLDivElement | null>(null)
  // Отдельный state для «Партнёр» попапа (add и edit) — временный выбор до нажатия «Сохранить»
  const [companyPickerOpen, setCompanyPickerOpen] = useState<null | 'add' | 'edit'>(null)
  const [companyPickerTempId, setCompanyPickerTempId] = useState<string>('')
  const [companyPickerSearch, setCompanyPickerSearch] = useState('')
  // Режим модалки: create (Добавить платёж) или edit (Редактировать платёж)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null)

  // Закрытие партнёрского dropdown по клику вне
  useEffect(() => {
    if (!companyDropdownOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setCompanyDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [companyDropdownOpen])
  const [events, setEvents] = useState<EventOption[]>([])
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null)

  // Load user role from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('admin_user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        setUserRole(user.role || '')
      } catch (e) {
        // Failed to parse user data
      }
    }
  }, [])

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Auto-dismiss notification after 4 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null)
      }, 4000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type })
  }

  const handleExport = async (ids?: string) => {
    try {
      const url = ids
        ? `/api/admin/donations/export?ids=${ids}`
        : '/api/admin/donations/export'

      const response = await adminApi.get(url, {
        responseType: 'blob'
      })

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = 'donations_export.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      showNotification('Ошибка при экспорте', 'error')
    }
  }

  const fetchDonations = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      params.append('limit', String(limit))
      params.append('offset', String(offset))
      if (filter.status) params.append('status', filter.status)
      if (filter.gateway) params.append('gateway', filter.gateway)
      if (filter.search) params.append('search', filter.search)
      if (filter.dateFrom) params.append('dateFrom', filter.dateFrom)
      if (filter.dateTo) params.append('dateTo', filter.dateTo)
      if (filter.treesFrom) params.append('treesFrom', filter.treesFrom)
      if (filter.treesTo) params.append('treesTo', filter.treesTo)
      if (filter.isPartner) params.append('isPartner', filter.isPartner)
      if (filter.recordType) params.append('recordType', filter.recordType)
      if (filter.regionId) params.append('regionId', filter.regionId)
      if (sortBy) {
        params.append('sortBy', sortBy)
        params.append('sortOrder', sortOrder)
      }

      const { data } = await adminApi.get(`/api/admin/donations?${params}`)
      setDonations(data.donations || [])
      setTotalCount(data.totalCount || 0)
    } catch (err) {
      // Error fetching donations
    } finally {
      setLoading(false)
    }
  }, [filter, offset, limit, sortBy, sortOrder])

  // Fetch statistics only once when component mounts (only for admin)
  useEffect(() => {
    if (userRole === 'admin') {
      const fetchStatistics = async () => {
        try {
          const { data } = await adminApi.get('/api/admin/donations/statistics')
          if (data) {
            setStatistics(data)
          }
        } catch (err) {
          // Error fetching statistics
        }
      }
      fetchStatistics()
    }
  }, [userRole])

  // Load events list once for the create-payment modal selector
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data } = await adminApi.get('/api/admin/events')
        if (Array.isArray(data?.events)) {
          setEvents(data.events.map((e: any) => ({
            id: e.id,
            name: e.name || `Событие #${e.id}`,
            isActiveDefault: !!e.isActiveDefault
          })))
        }
      } catch (err) {
        // Silently ignore — selector just won't be populated
      }
    }
    fetchEvents()
    ;(async () => {
      try {
        const { data } = await adminApi.get<{ regions: RegionOption[] }>('/api/regions')
        setRegions(data.regions || [])
      } catch {
        // ignore — filter just won't show region options
      }
    })()
  }, [])

  // Reset offset when filters or limit change
  useEffect(() => {
    setOffset(0)
  }, [filter.status, filter.gateway, filter.search, filter.dateFrom, filter.dateTo, filter.treesFrom, filter.treesTo, filter.recordType, filter.regionId, limit])

  useEffect(() => {
    fetchDonations()
    setSelectedIds(new Set()) // Clear selection when changing page or filters
  }, [fetchDonations])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ru-RU').format(amount) + ' сом'
  }

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      'succeeded': 'Оплачен',
      'paid': 'Оплачен',
      'pending': 'Ожидание',
      'failed': 'Ошибка',
      'refunded': 'Возврат'
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'succeeded': '#52c41a',
      'paid': '#52c41a',
      'pending': '#faad14',
      'failed': '#ff4d4f',
      'refunded': '#722ed1'
    }
    return colorMap[status] || '#666'
  }

  const getGatewayLabel = (gateway: string) => {
    const gatewayMap: Record<string, string> = {
      'freedompay': 'FreedomPay',
      'finik': 'FINIK',
      'barter': 'Бартер',
      'bank_transfer': 'Банк. перевод'
    }
    return gatewayMap[gateway] || gateway
  }

  const getInstagram = (url: string) => {
    const match = url.match(/instagram\.com\/([^/?]+)/)
    const username = match ? match[1] : url
    const fullUrl = url.startsWith('http') ? url : `https://instagram.com/${url}`
    return { username, url: fullUrl }
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === donations.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(donations.map(d => d.order_id)))
    }
  }

  const toggleSelectOne = (orderId: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId)
    } else {
      newSelected.add(orderId)
    }
    setSelectedIds(newSelected)
  }

  const isAllSelected = donations.length > 0 && selectedIds.size === donations.length

  const openModal = () => {
    const defaultEvent = events.find(e => e.isActiveDefault) || events[0]
    setShowModal(true)
    setModalMode('create')
    setEditingOrderId(null)
    setCompanySearch('')
    setCompanyDropdownOpen(false)
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      instagram: '',
      gateway: 'bank_transfer',
      treesCount: '',
      amount: '',
      barterComment: '',
      isPartner: false,
      organizationName: '',
      eventId: defaultEvent ? String(defaultEvent.id) : '',
      companyId: ''
    })
    setFormErrors({})
    if (companies.length === 0) {
      adminApi.get<{ companies: CompanyOption[] }>('/api/admin/companies')
        .then(({ data }) => setCompanies(data.companies || []))
        .catch(() => {})
    }
  }

  const openEditModal = (donation: Donation) => {
    const manualGateway = donation.gateway === 'barter' ? 'barter' : 'bank_transfer'
    const eventIdForEdit = donation.event_id != null ? String(donation.event_id) : ''
    setFormData({
      firstName: donation.first_name || '',
      lastName: donation.last_name || '',
      email: donation.email || '',
      phone: donation.phone || '',
      instagram: donation.instagram_url || '',
      gateway: manualGateway,
      treesCount: donation.trees_count != null ? String(donation.trees_count) : '',
      amount: donation.amount_kgs != null ? String(donation.amount_kgs) : '',
      barterComment: manualGateway === 'barter' ? (donation.comment || '') : '',
      isPartner: !!donation.is_partner,
      organizationName: donation.company_name || donation.organization_name || '',
      eventId: eventIdForEdit,
      companyId: donation.company_id ? String(donation.company_id) : ''
    })
    setCompanySearch('')
    setCompanyDropdownOpen(false)
    setFormErrors({})
    setModalMode('edit')
    setEditingOrderId(donation.order_id)
    setDetailsModalOpen(false)
    setShowModal(true)
    if (companies.length === 0) {
      adminApi.get<{ companies: CompanyOption[] }>('/api/admin/companies')
        .then(({ data }) => setCompanies(data.companies || []))
        .catch(() => {})
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setFormErrors({})
  }

  const openRepeatModal = (donation: Donation) => {
    const manualGateway = donation.gateway === 'barter' ? 'barter' : 'bank_transfer'
    const defaultEvent = events.find(e => e.isActiveDefault) || events[0]
    const eventIdForRepeat = donation.event_id != null
      ? String(donation.event_id)
      : (defaultEvent ? String(defaultEvent.id) : '')
    setFormData({
      firstName: donation.first_name || '',
      lastName: donation.last_name || '',
      email: donation.email || '',
      phone: donation.phone || '',
      instagram: donation.instagram_url || '',
      gateway: manualGateway,
      treesCount: donation.trees_count != null ? String(donation.trees_count) : '',
      amount: donation.amount_kgs != null ? String(donation.amount_kgs) : '',
      barterComment: manualGateway === 'barter' ? (donation.comment || '') : '',
      isPartner: !!donation.is_partner,
      organizationName: donation.organization_name || '',
      eventId: eventIdForRepeat,
      companyId: ''
    })
    setCompanySearch('')
    setCompanyDropdownOpen(false)
    setFormErrors({})
    setDetailsModalOpen(false)
    setIsEditing(false)
    setShowModal(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    setFormData(prev => ({ ...prev, [name]: newValue }))
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleTreesChange = (v: string) => {
    const n = parseInt(v, 10)
    setFormData(prev => ({
      ...prev,
      treesCount: v,
      amount: Number.isFinite(n) && n > 0 ? String(n * TREE_PRICE) : prev.amount
    }))
    if (formErrors.treesCount) setFormErrors(prev => ({ ...prev, treesCount: '' }))
  }

  const handleAmountChange = (v: string) => {
    const n = parseFloat(v)
    setFormData(prev => ({
      ...prev,
      amount: v,
      treesCount: Number.isFinite(n) && n > 0 ? String(Math.round(n / TREE_PRICE)) : prev.treesCount
    }))
    if (formErrors.amount) setFormErrors(prev => ({ ...prev, amount: '' }))
  }

  const handleInstagramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (!value.trim()) {
      setFormData(prev => ({ ...prev, instagram: '' }))
      return
    }

    let cleanValue = value.trim()

    // Если пользователь ввел полный URL
    if (cleanValue.includes('instagram.com')) {
      let url = cleanValue
      // Добавляем https:// если нет протокола
      if (!url.startsWith('http')) {
        url = 'https://' + url
      }
      // Убираем лишнюю @ из URL типа https://www.instagram.com/@username
      url = url.replace('instagram.com/@', 'instagram.com/')
      setFormData(prev => ({ ...prev, instagram: url }))
    }
    // Если ввел @username
    else if (cleanValue.startsWith('@')) {
      const username = cleanValue.slice(1)
      setFormData(prev => ({ ...prev, instagram: `https://www.instagram.com/${username}` }))
    }
    // Если ввел просто username
    else {
      setFormData(prev => ({ ...prev, instagram: `https://www.instagram.com/${cleanValue}` }))
    }

    if (formErrors.instagram) {
      setFormErrors(prev => ({ ...prev, instagram: '' }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors: Record<string, string> = {}
    // ФИО/email/phone обязательны всегда — данные не теряются при переключении isPartner
    if (!formData.firstName.trim()) errors.firstName = 'Обязательное поле'
    if (!formData.lastName.trim()) errors.lastName = 'Обязательное поле'
    if (!formData.email.trim()) errors.email = 'Обязательное поле'
    if (!formData.phone.trim()) errors.phone = 'Обязательное поле'
    if (!formData.gateway) errors.gateway = 'Обязательное поле'
    if (!formData.treesCount || parseInt(formData.treesCount) < 1) errors.treesCount = 'Минимум 1 дерево'
    if (!formData.amount || parseFloat(formData.amount) < 1) errors.amount = 'Минимум 1 сом'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    setSubmitting(true)
    try {
      const { barterComment, organizationName, eventId, companyId, ...rest } = formData
      const isPartner = !!formData.isPartner
      const submitData: Record<string, unknown> = {
        ...rest,
        isBarter: formData.gateway === 'barter',
        comment: barterComment,
        isPartner,
        organizationName: organizationName || undefined,
        eventId: eventId || undefined,
        companyId: isPartner ? (companyId || null) : null
      }

      if (modalMode === 'edit' && editingOrderId) {
        // PUT — обновляем все редактируемые поля платежа
        const putData: Record<string, unknown> = {
          isPartner,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          instagram: formData.instagram,
          gateway: formData.gateway,
          isBarter: formData.gateway === 'barter',
          comment: barterComment || null,
          treesCount: parseInt(formData.treesCount, 10),
          amount: parseFloat(formData.amount),
          eventId: eventId || null,
          companyId: isPartner ? (companyId || null) : null
        }
        await adminApi.put(`/api/admin/donations/${editingOrderId}`, putData)
        showNotification('Платеж обновлён!', 'success')
      } else {
        await adminApi.post('/api/admin/donations', submitData)
        showNotification('Платеж успешно добавлен!', 'success')
      }
      closeModal()
      fetchDonations()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка при сохранении платежа')
        : 'Ошибка при сохранении платежа'
      showNotification(errorMessage, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSavePartner = async () => {
    if (!selectedDonation) return

    setSavingPartner(true)
    try {
      const updateData: {
        isPartner: boolean;
        organizationName?: string;
        firstName?: string;
        lastName?: string;
        companyId?: string | null
      } = {
        isPartner: editedIsPartner
      }

      // Если редактируется, отправляем все измененные поля
      if (isEditing) {
        updateData.organizationName = editedOrganizationName
        updateData.firstName = editedFirstName
        updateData.lastName = editedLastName
        // companyId: если is_partner=true и выбран из dropdown — пришлём id.
        // Если is_partner=false — обнулим (null) чтобы отвязать от компании.
        updateData.companyId = editedIsPartner ? (editedCompanyId || null) : null
      } else if (!editedIsPartner) {
        // Смена флага в false без входа в режим редактирования — тоже отвязываем
        updateData.companyId = null
      }

      await adminApi.put(`/api/admin/donations/${selectedDonation.order_id}`, updateData)

      showNotification('Изменения сохранены!', 'success')

      // Update the donation in the list
      setDonations(prev => prev.map(d =>
        d.order_id === selectedDonation.order_id
          ? {
              ...d,
              is_partner: editedIsPartner,
              organization_name: isEditing ? editedOrganizationName : d.organization_name,
              first_name: isEditing ? editedFirstName : d.first_name,
              last_name: isEditing ? editedLastName : d.last_name
            }
          : d
      ))

      // Update selectedDonation
      setSelectedDonation(prev => prev ? {
        ...prev,
        is_partner: editedIsPartner,
        organization_name: isEditing ? editedOrganizationName : prev.organization_name,
        first_name: isEditing ? editedFirstName : prev.first_name,
        last_name: isEditing ? editedLastName : prev.last_name
      } : null)

      setDetailsModalOpen(false)
      setIsEditing(false)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка при сохранении')
        : 'Ошибка при сохранении'
      showNotification(errorMessage, 'error')
    } finally {
      setSavingPartner(false)
    }
  }

  const handleSaveInstagram = async () => {
    if (!selectedDonation) return

    setSavingInstagram(true)
    try {
      const response = await adminApi.put(`/api/admin/donations/${selectedDonation.order_id}`, {
        isPartner: selectedDonation.is_partner,
        instagramUrl: editedInstagramUrl
      })
      const normalizedInstagram: string | null = response.data?.instagramUrl ?? null

      showNotification('Instagram сохранён!', 'success')

      setDonations(prev => prev.map(d =>
        d.order_id === selectedDonation.order_id
          ? { ...d, instagram_url: normalizedInstagram }
          : d
      ))
      setSelectedDonation(prev => prev ? { ...prev, instagram_url: normalizedInstagram } : null)
      setIsEditingInstagram(false)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка при сохранении')
        : 'Ошибка при сохранении'
      showNotification(errorMessage, 'error')
    } finally {
      setSavingInstagram(false)
    }
  }

  const handleSortByTrees = () => {
    if (sortBy === 'trees') {
      if (sortOrder === 'desc') {
        // First click -> desc, second click -> asc
        setSortOrder('asc')
      } else {
        // Third click -> reset to default (no sorting)
        setSortBy(null)
        setSortOrder('desc')
      }
    } else {
      // First click -> set sort to desc
      setSortBy('trees')
      setSortOrder('desc')
    }
    setOffset(0)
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Notification Toast */}
      {notification && (
        <div
          className={`fixed top-6 right-6 z-[2000] rounded-lg px-5 py-4 shadow-lg flex items-center gap-3 min-w-[320px] max-w-[500px] border ${
            notification.type === 'success' ? 'bg-[#f6ffed] border-[#b7eb8f]' : 'bg-[#fff2f0] border-[#ffccc7]'
          }`}
        >
          {notification.type === 'success' ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="flex-none">
              <circle cx="12" cy="12" r="10" fill="#52c41a"/>
              <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="flex-none">
              <circle cx="12" cy="12" r="10" fill="#ff4d4f"/>
              <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
          <span className={`text-sm font-medium flex-1 ${notification.type === 'success' ? 'text-[#52c41a]' : 'text-[#ff4d4f]'}`}>
            {notification.message}
          </span>
          <button
            onClick={() => setNotification(null)}
            className={`bg-transparent border-0 text-lg cursor-pointer p-0 leading-none opacity-60 ${
              notification.type === 'success' ? 'text-[#52c41a]' : 'text-[#ff4d4f]'
            }`}
          >
            ×
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="page-header flex-none flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Платежи и Донаты</h1>
          <p className="page-subtitle">
            Управление платежами, отслеживание статусов и экспорт данных
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 items-center">
          <button
            onClick={openModal}
            className="btn-primary"
          >
            Добавить платеж
          </button>

          {selectedIds.size > 0 && (
            <button
              onClick={() => {
                const ids = Array.from(selectedIds).join(',')
                handleExport(ids)
              }}
              className="btn-secondary px-4 py-2 text-sm"
            >
              Экспорт выбранных
            </button>
          )}

          <button
            onClick={() => handleExport()}
            className="btn-secondary px-4 py-2 text-sm"
          >
            Экспорт всех
          </button>
        </div>
      </div>

      {/* Statistics Cards - Only for admin */}
      {userRole === 'admin' && (
        <div
          className="grid gap-4 mb-6 flex-none"
          // dynamic: auto-fit grid
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}
        >
          <div className="admin-card">
            <div className="stat-label">
              Всего платежей
            </div>
            <div className="stat-value text-[#1890ff]">
              {animatedTotalPayments.toLocaleString('ru-RU')}
            </div>
          </div>

          <div className="admin-card">
            <div className="stat-label">
              Деревьев посажено
            </div>
            <div className="stat-value text-[#1890ff]">
              {animatedTreesPlanted.toLocaleString('ru-RU')}
            </div>
          </div>

          <div className="admin-card">
            <div className="stat-label">
              Общая сумма
            </div>
            <div className="stat-value text-[#1890ff]">
              {animatedTotalAmount.toLocaleString('ru-RU')} сом
            </div>
          </div>

          <div className="admin-card">
            <div className="stat-label">
              Уникальных доноров
            </div>
            <div className="stat-value text-[#1890ff]">
              {animatedUniqueDonors.toLocaleString('ru-RU')}
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-none">
        {/* Left - Search, Filter, Sort */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <input
            id="filterSearch"
            type="text"
            name="filterSearch"
            placeholder="Поиск..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            className="form-control w-[280px]"
            autoComplete="off"
          />

          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary px-4 py-2 text-sm flex items-center gap-1.5 ${
              showFilters
                ? 'bg-[#e6f7ff] border-[#40a9ff] text-[#1890ff]'
                : 'bg-white border-[#d9d9d9] text-black/65'
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M1.5 3.5h13M3.5 8h9M5.5 12.5h5"/>
            </svg>
            Фильтры
            {(filter.status || filter.gateway || filter.isPartner || filter.recordType || filter.regionId || filter.dateFrom || filter.dateTo || filter.treesFrom || filter.treesTo) && (
              <span className="bg-[#ff4d4f] text-white rounded-[10px] px-1.5 py-0.5 text-[11px] font-semibold">
                •
              </span>
            )}
          </button>

          {/* Sort Dropdown */}
          <select
            value={sortBy ? `${sortBy}_${sortOrder}` : ''}
            onChange={(e) => {
              const value = e.target.value
              if (!value) {
                setSortBy(null)
                setSortOrder('desc')
              } else {
                const [field, order] = value.split('_')
                setSortBy(field as 'trees')
                setSortOrder(order as 'asc' | 'desc')
              }
            }}
            className="form-control min-w-[180px] h-10 px-3 py-2 text-sm cursor-pointer"
          >
            <option value="">По дате (новые)</option>
            <option value="trees_desc">По деревьям (больше)</option>
            <option value="trees_asc">По деревьям (меньше)</option>
          </select>

        </div>

        {/* Right - Pagination */}
        <div className="flex items-center gap-1.5 flex-nowrap">
          {/* Limit Selector */}
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="form-control min-w-[60px] w-[60px] h-7 px-1 py-0.5 text-xs cursor-pointer"
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
          </select>
          <span className="text-[13px] text-gray-500 whitespace-nowrap">
            {Math.min(offset + donations.length, totalCount)} из {totalCount}
          </span>
          <button
            onClick={(e) => {
              e.preventDefault()
              setOffset(Math.max(0, offset - limit))
            }}
            disabled={offset === 0}
            className={`btn-secondary px-2.5 py-1 text-[13px] ${offset === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            ←
          </button>
          <span className="text-[13px] text-gray-700 px-1">
            {Math.floor(offset / limit) + 1}
          </span>
          <button
            onClick={(e) => {
              e.preventDefault()
              setOffset(Math.min(offset + limit, Math.max(0, totalCount - limit)))
            }}
            disabled={offset + limit >= totalCount}
            className={`btn-secondary px-2.5 py-1 text-[13px] ${offset + limit >= totalCount ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            →
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-[#fafafa] p-4 rounded-lg mb-4 border border-[#f0f0f0] flex-none flex gap-4 items-end overflow-x-auto">
          {/* Status Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-500 font-medium">Статус</label>
            <div className="relative min-w-[130px]">
              <select
                id="filterStatus"
                name="filterStatus"
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="form-control w-full pr-8 appearance-none"
                autoComplete="off"
              >
                <option value="">Все</option>
                <option value="succeeded">Оплачен</option>
                <option value="pending">Ожидание</option>
                <option value="failed">Ошибка</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4.5L6 7.5L9 4.5"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Gateway Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-500 font-medium">Способ оплаты</label>
            <div className="relative min-w-[150px]">
              <select
                id="filterGateway"
                name="filterGateway"
                value={filter.gateway}
                onChange={(e) => setFilter({ ...filter, gateway: e.target.value })}
                className="form-control w-full pr-8 appearance-none"
                autoComplete="off"
              >
                <option value="">Все</option>
                <option value="finik">FINIK</option>
                <option value="bank_transfer">Банк. перевод</option>
                <option value="barter">Бартер</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4.5L6 7.5L9 4.5"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Partner Type Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-500 font-medium">Тип</label>
            <div className="relative min-w-[130px]">
              <select
                id="filterIsPartner"
                name="filterIsPartner"
                value={filter.isPartner}
                onChange={(e) => setFilter({ ...filter, isPartner: e.target.value })}
                className="form-control w-full pr-8 appearance-none"
                autoComplete="off"
              >
                <option value="">Все</option>
                <option value="true">Партнер</option>
                <option value="false">Обычный</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4.5L6 7.5L9 4.5"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Record Type Filter (Certificate/Ticket) */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-500 font-medium">Документ</label>
            <div className="relative min-w-[140px]">
              <select
                id="filterRecordType"
                name="filterRecordType"
                value={filter.recordType}
                onChange={(e) => setFilter({ ...filter, recordType: e.target.value })}
                className="form-control w-full pr-8 appearance-none"
                autoComplete="off"
              >
                <option value="">Все</option>
                <option value="certificate">Сертификат</option>
                <option value="ticket">Билет</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4.5L6 7.5L9 4.5"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Region Filter */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-500 font-medium">Область</label>
            <div className="relative min-w-[160px]">
              <select
                id="filterRegion"
                name="filterRegion"
                value={filter.regionId}
                onChange={(e) => setFilter({ ...filter, regionId: e.target.value })}
                className="form-control w-full pr-8 appearance-none"
                autoComplete="off"
              >
                <option value="">Все</option>
                <option value="null">Кыргызстан</option>
                {regions.map(r => (
                  <option key={r.id} value={String(r.id)}>{r.name?.ru || r.code}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4.5L6 7.5L9 4.5"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Date Range */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-500 font-medium">Период</label>
            <button
              onClick={() => {
                setTempDateFrom(filter.dateFrom)
                setTempDateTo(filter.dateTo)
                setShowDateModal(true)
              }}
              className="form-control min-w-[200px] h-10 flex items-center justify-between px-3 py-2 cursor-pointer text-left bg-white"
            >
              <span className={`text-[13px] ${filter.dateFrom || filter.dateTo ? 'text-black/85' : 'text-black/45'}`}>
                {filter.dateFrom || filter.dateTo
                  ? `${filter.dateFrom ? new Date(filter.dateFrom).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '...'} — ${filter.dateTo ? new Date(filter.dateTo).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '...'}`
                  : 'Выбрать'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 4.5L6 7.5L9 4.5"/>
              </svg>
            </button>
          </div>

          {/* Trees Range */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-500 font-medium">Деревья</label>
            <button
              onClick={() => {
                setTempTreesFrom(filter.treesFrom)
                setTempTreesTo(filter.treesTo)
                setShowTreesModal(true)
              }}
              className="form-control min-w-[150px] h-10 flex items-center justify-between px-3 py-2 cursor-pointer text-left bg-white"
            >
              <span className={`text-[13px] ${filter.treesFrom || filter.treesTo ? 'text-black/85' : 'text-black/45'}`}>
                {filter.treesFrom || filter.treesTo
                  ? `${filter.treesFrom || '0'} — ${filter.treesTo || '∞'}`
                  : 'Выбрать'}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 4.5L6 7.5L9 4.5"/>
              </svg>
            </button>
          </div>

          <div className="flex-1" />

          {/* Clear Filters */}
          {(filter.status || filter.gateway || filter.isPartner || filter.recordType || filter.regionId || filter.dateFrom || filter.dateTo || filter.treesFrom || filter.treesTo) && (
            <button
              onClick={() => setFilter({
                status: '',
                gateway: '',
                search: filter.search,
                dateFrom: '',
                dateTo: '',
                treesFrom: '',
                treesTo: '',
                isPartner: '',
                recordType: '',
                regionId: ''
              })}
              className="btn-secondary px-3 py-2 text-[13px] text-[#ff4d4f] border-[#ffccc7]"
            >
              Сбросить
            </button>
          )}
        </div>
      )}

      {/* Trees Range Modal */}
      {showTreesModal && (
        <div
          className="fixed inset-0 bg-black/45 flex items-center justify-center z-[1000]"
          onClick={() => setShowTreesModal(false)}
        >
          <div
            className="bg-white rounded-lg p-6 w-[360px] shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mt-0 mb-4 mx-0 text-base font-semibold">Фильтр по деревьям</h3>

            <div className="flex flex-col gap-3 mb-5">
              <div>
                <label className="block text-[13px] text-gray-500 mb-1.5">От</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Минимум"
                  value={tempTreesFrom}
                  onChange={(e) => setTempTreesFrom(e.target.value)}
                  min="0"
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-[13px] text-gray-500 mb-1.5">До</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Максимум"
                  value={tempTreesTo}
                  onChange={(e) => setTempTreesTo(e.target.value)}
                  min="0"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setTempTreesFrom('')
                  setTempTreesTo('')
                  setFilter({ ...filter, treesFrom: '', treesTo: '' })
                  setShowTreesModal(false)
                }}
                className="btn-secondary px-4 py-2 text-sm"
              >
                Сбросить
              </button>
              <button
                onClick={() => {
                  setFilter({ ...filter, treesFrom: tempTreesFrom, treesTo: tempTreesTo })
                  setShowTreesModal(false)
                }}
                className="btn-primary px-4 py-2 text-sm"
              >
                Применить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Modal */}
      {showDateModal && (
        <div
          className="fixed inset-0 bg-black/45 flex items-center justify-center z-[1000]"
          onClick={() => setShowDateModal(false)}
        >
          <div
            className="bg-white rounded-lg p-6 w-[360px] shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mt-0 mb-4 mx-0 text-base font-semibold">Фильтр по периоду</h3>

            <div className="flex flex-col gap-3 mb-5">
              <div>
                <label className="block text-[13px] text-gray-500 mb-1.5">С</label>
                <input
                  type="date"
                  className="form-control"
                  value={tempDateFrom}
                  onChange={(e) => setTempDateFrom(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-[13px] text-gray-500 mb-1.5">По</label>
                <input
                  type="date"
                  className="form-control"
                  value={tempDateTo}
                  onChange={(e) => setTempDateTo(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setTempDateFrom('')
                  setTempDateTo('')
                  setFilter({ ...filter, dateFrom: '', dateTo: '' })
                  setShowDateModal(false)
                }}
                className="btn-secondary px-4 py-2 text-sm"
              >
                Сбросить
              </button>
              <button
                onClick={() => {
                  setFilter({ ...filter, dateFrom: tempDateFrom, dateTo: tempDateTo })
                  setShowDateModal(false)
                }}
                className="btn-primary px-4 py-2 text-sm"
              >
                Применить
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-20 flex-1">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : (
        <>
          <div className="table-card flex-none">
          <div ref={tableContainerRef} className="overflow-x-auto overflow-y-visible">
            <table className="admin-table min-w-full table-auto whitespace-nowrap">
              <thead>
                <tr>
                  <th
                    className="w-[50px] min-w-[50px] max-w-[50px] whitespace-nowrap text-center bg-[#fafafa]"
                    // dynamic: sticky positioning conditional on isMobile
                    style={{
                      position: isMobile ? 'static' : 'sticky',
                      left: isMobile ? 'auto' : 0,
                      zIndex: isMobile ? 'auto' : 10,
                      boxShadow: isMobile ? 'none' : '2px 0 4px rgba(0,0,0,0.05)',
                    }}
                  >
                    <input
                      id="selectAll"
                      type="checkbox"
                      name="selectAll"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      autoComplete="off"
                      className="cursor-pointer"
                    />
                  </th>
                  <th
                    className="w-[200px] min-w-[200px] max-w-[200px] whitespace-nowrap bg-[#fafafa]"
                    // dynamic: sticky positioning conditional on isMobile
                    style={{
                      position: isMobile ? 'static' : 'sticky',
                      left: isMobile ? 'auto' : '50px',
                      zIndex: isMobile ? 'auto' : 10,
                      boxShadow: isMobile ? 'none' : '2px 0 4px rgba(0,0,0,0.05)',
                    }}
                  >Донор</th>
                  <th
                    className="min-w-[100px] whitespace-nowrap cursor-pointer select-none"
                    onClick={handleSortByTrees}
                  >
                    Деревьев{' '}
                    <span className={`ml-1 ${sortBy === 'trees' ? 'text-forest font-bold' : 'text-gray-500 font-normal'}`}>
                      {sortBy === 'trees' ? (sortOrder === 'desc' ? '▼' : '▲') : '⇅'}
                    </span>
                  </th>
                  <th className="min-w-[120px] whitespace-nowrap">Сумма</th>
                  <th className="min-w-[130px] whitespace-nowrap">Область</th>
                  <th
                    className="min-w-[120px] whitespace-nowrap bg-[#fafafa]"
                    // dynamic: sticky positioning conditional on isMobile
                    style={{
                      position: isMobile ? 'static' : 'sticky',
                      left: isMobile ? 'auto' : '250px',
                      zIndex: isMobile ? 'auto' : 10,
                      boxShadow: isMobile ? 'none' : '2px 0 4px rgba(0,0,0,0.05)',
                    }}
                  >Статус</th>
                  <th className="min-w-[180px] whitespace-nowrap">Сертификат</th>
                  <th className="min-w-[180px] whitespace-nowrap">Билет</th>
                  <th className="min-w-[150px] whitespace-nowrap">Способ</th>
                  <th className="min-w-[120px] whitespace-nowrap">Instagram</th>
                  <th className="min-w-[130px] whitespace-nowrap">Телефон</th>
                  <th className="min-w-[220px] whitespace-nowrap">Email</th>
                  <th
                    className="min-w-[150px] whitespace-nowrap bg-[#fafafa]"
                    // dynamic: sticky positioning conditional on isMobile
                    style={{
                      position: isMobile ? 'static' : 'sticky',
                      right: isMobile ? 'auto' : 0,
                      zIndex: isMobile ? 'auto' : 10,
                      boxShadow: isMobile ? 'none' : '-2px 0 4px rgba(0,0,0,0.05)',
                    }}
                  >Создано</th>
                </tr>
              </thead>
              <tbody>
                {donations.length === 0 ? (
                  <tr>
                    <td colSpan={13}>
                      <div className="empty-state">
                        <div className="empty-state-text">Платежи не найдены</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  donations.map(donation => {
                    const instagram = donation.instagram_url ? getInstagram(donation.instagram_url) : null
                    return (
                      <tr
                        key={donation.order_id}
                        onClick={() => {
                          setSelectedDonation(donation)
                          setEditedIsPartner(donation.is_partner)
                          setIsEditing(false)
                          setIsEditingInstagram(false)
                          setEditedOrganizationName(donation.organization_name || '')
                          setEditedFirstName(donation.first_name)
                          setEditedLastName(donation.last_name)
                          setEditedCompanyId(donation.company_id ? String(donation.company_id) : '')
                          setEditedCompanySearch(donation.company_name?.trim() || donation.organization_name || '')
                          setEditedCompanyDropdownOpen(false)
                          setDetailsModalOpen(true)
                          // Ленивая загрузка справочника компаний
                          if (companies.length === 0) {
                            adminApi.get<{ companies: CompanyOption[] }>('/api/admin/companies')
                              .then(({ data }) => setCompanies(data.companies || []))
                              .catch(() => {})
                          }
                        }}
                        className="cursor-pointer"
                      >
                        <td
                          onClick={(e) => e.stopPropagation()}
                          className="bg-white w-[50px] min-w-[50px] max-w-[50px] text-center whitespace-nowrap"
                          // dynamic: sticky positioning conditional on isMobile
                          style={{
                            position: isMobile ? 'static' : 'sticky',
                            left: isMobile ? 'auto' : 0,
                            zIndex: isMobile ? 'auto' : 9,
                            boxShadow: isMobile ? 'none' : '2px 0 4px rgba(0,0,0,0.05)',
                          }}
                        >
                          <input
                            id={`selectDonation${donation.order_id}`}
                            type="checkbox"
                            name={`selectDonation${donation.order_id}`}
                            checked={selectedIds.has(donation.order_id)}
                            onChange={() => toggleSelectOne(donation.order_id)}
                            autoComplete="off"
                            className="cursor-pointer"
                          />
                        </td>
                        <td
                          className="bg-white font-medium w-[200px] min-w-[200px] max-w-[200px] h-[60px] px-3 py-2 align-middle"
                          // dynamic: sticky positioning conditional on isMobile
                          style={{
                            position: isMobile ? 'static' : 'sticky',
                            left: isMobile ? 'auto' : '50px',
                            zIndex: isMobile ? 'auto' : 9,
                            boxShadow: isMobile ? 'none' : '2px 0 4px rgba(0,0,0,0.05)',
                          }}
                          title={donorLabel(donation)}
                        >
                          <div className="whitespace-normal break-words leading-tight max-h-[44px] overflow-hidden block">
                            {donorLabel(donation)}
                          </div>
                        </td>
                        <td className="text-center whitespace-nowrap">{donation.trees_count}</td>
                        <td className="whitespace-nowrap">{formatAmount(donation.amount_kgs)}</td>
                        <td className="whitespace-nowrap text-[13px]">
                          {donation.region_id
                            ? (donation.region_name?.ru || donation.region_name?.en || donation.region_code || '—')
                            : <span className="text-gray-400">Весь КР</span>}
                        </td>
                        <td
                          className={`whitespace-nowrap ${isMobile ? 'bg-transparent' : 'bg-white'}`}
                          // dynamic: sticky positioning conditional on isMobile
                          style={{
                            position: isMobile ? 'static' : 'sticky',
                            left: isMobile ? 'auto' : '250px',
                            zIndex: isMobile ? 'auto' : 9,
                            boxShadow: isMobile ? 'none' : '2px 0 4px rgba(0,0,0,0.05)',
                          }}
                        >
                          <span
                            className="inline-block px-3 py-1 rounded-xl text-[13px]"
                            // dynamic: color derived from status
                            style={{
                              background: getStatusColor(donation.status) + '20',
                              color: getStatusColor(donation.status),
                            }}
                          >
                            {getStatusLabel(donation.status)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap relative overflow-visible">
                          {donation.certificate_number ? (
                            <div className="relative">
                              <span
                                onClick={(e) => {
                                  e.stopPropagation()
                                  // Определяем позицию попапа
                                  const target = e.currentTarget as HTMLSpanElement
                                  const triggerRect = target.getBoundingClientRect()
                                  const popupHeight = 120 // Примерная высота попапа с запасом

                                  // Проверяем пространство ниже элемента до конца окна
                                  const spaceBelow = window.innerHeight - triggerRect.bottom

                                  // Если достаточно места снизу, показываем под элементом, иначе - сверху
                                  setPopupPosition(spaceBelow >= popupHeight ? 'bottom' : 'top')
                                  setCertificatePopup(donation.certificate_number!)
                                }}
                                className="text-[#1890ff] cursor-pointer underline"
                              >
                                {donation.certificate_number}
                              </span>
                              <span className="ml-1">
                                {donation.certificate_sent_at ? '✅' : '⏛'}
                              </span>
                              {certificatePopup === donation.certificate_number && (
                                <>
                                  <div
                                    className="fixed inset-0 z-[999] cursor-default"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setCertificatePopup(null)
                                    }}
                                  />
                                  <div
                                    className={`absolute left-0 bg-white border border-[#d9d9d9] rounded-lg shadow-lg z-[1000] min-w-[180px] ${
                                      popupPosition === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1'
                                    }`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <a
                                      href={`${API_URL}/api/certificate/${donation.certificate_number}/jpeg`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block px-4 py-2.5 text-[#333] no-underline text-sm border-b border-[#f0f0f0] transition-colors hover:bg-[#f5f5f5]"
                                    >
                                      Скачать JPEG
                                    </a>
                                    <a
                                      href={`${API_URL}/api/certificate/${donation.certificate_number}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block px-4 py-2.5 text-[#333] no-underline text-sm transition-colors hover:bg-[#f5f5f5]"
                                    >
                                      Скачать PDF файл
                                    </a>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          {donation.ticket_number ? (
                            <a
                              href={`${API_URL}/api/tickets/${donation.ticket_number}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[#1890ff] cursor-pointer underline"
                            >
                              {donation.ticket_number}
                            </a>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          {getGatewayLabel(donation.gateway)}
                          {donation.gateway === 'barter' && donation.comment && (
                            <span className="ml-1.5 text-base" title="Есть комментарий">💬</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          {instagram ? (
                            <a
                              href={instagram.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#1890ff] no-underline"
                            >
                              {instagram.username}
                            </a>
                          ) : '-'}
                        </td>
                        <td className="whitespace-nowrap">{donation.phone || '-'}</td>
                        <td className="text-[13px] whitespace-nowrap">{donation.email || '-'}</td>
                        <td
                          className="bg-white text-[13px] whitespace-nowrap"
                          // dynamic: sticky positioning conditional on isMobile
                          style={{
                            position: isMobile ? 'static' : 'sticky',
                            right: isMobile ? 'auto' : 0,
                            zIndex: isMobile ? 'auto' : 9,
                            boxShadow: isMobile ? 'none' : '-2px 0 4px rgba(0,0,0,0.05)',
                          }}
                        >
                          {formatDate(donation.created_at)}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Add Payment Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg w-[90%] max-w-[600px] max-h-[90vh] shadow-md flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-lg">
              <h2 className="text-lg font-semibold m-0">{modalMode === 'edit' ? 'Редактировать платёж' : 'Добавить платёж'}</h2>
              <button
                onClick={closeModal}
                className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="px-5 py-5 overflow-y-auto flex-1">
                {/* Чекбокс Партнёрский платёж */}
                <label className="admin-checkbox-row mb-4">
                  <input
                    type="checkbox"
                    className="admin-checkbox"
                    checked={!!formData.isPartner}
                    onChange={(e) => setFormData(p => ({
                      ...p,
                      isPartner: e.target.checked,
                      // при снятии галочки — отвязываем компанию
                      companyId: e.target.checked ? p.companyId : ''
                    }))}
                  />
                  <div>
                    <div className="admin-checkbox-title">Партнёрский платёж</div>
                    <div className="admin-checkbox-hint">
                      Если платит организация — отметьте и выберите её из справочника (необязательно).
                    </div>
                  </div>
                </label>

                {/* Компания (только для партнёрских) */}
                {formData.isPartner && (
                  <div className="mb-4">
                    <label className="block mb-1.5 text-sm font-medium text-black/85">
                      Компания
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyPickerTempId(formData.companyId || '')
                        setCompanyPickerSearch('')
                        setCompanyPickerOpen('add')
                      }}
                      className="form-control text-left flex items-center gap-2"
                    >
                      {formData.companyId ? (
                        (() => {
                          const c = companies.find(x => String(x.id) === formData.companyId)
                          return c ? (
                            <>
                              {c.logo_url && (
                                <img src={c.logo_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                              )}
                              <span className="flex-1 truncate">{c.name}</span>
                              <span className="text-gray-400">▾</span>
                            </>
                          ) : (
                            <span className="flex-1 text-gray-400">Выбрать компанию…</span>
                          )
                        })()
                      ) : (
                        <>
                          <span className="flex-1 text-gray-400">Выбрать компанию…</span>
                          <span className="text-gray-400">▾</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-1">
                      Если нужной компании нет в справочнике — добавьте её в разделе «Партнёры».
                    </p>
                  </div>
                )}

                {/* Всегда показываем ФИО/email/phone/Instagram */}
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="firstName" className="block mb-1.5 text-sm font-medium text-black/85">
                          Имя <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={formErrors.firstName ? 'form-control border-red-500' : 'form-control'}
                          autoComplete="given-name"
                        />
                        {formErrors.firstName && (
                          <div className="field-error">
                            {formErrors.firstName}
                          </div>
                        )}
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block mb-1.5 text-sm font-medium text-black/85">
                          Фамилия <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={formErrors.lastName ? 'form-control border-red-500' : 'form-control'}
                          autoComplete="family-name"
                        />
                        {formErrors.lastName && (
                          <div className="field-error">
                            {formErrors.lastName}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="email" className="block mb-1.5 text-sm font-medium text-black/85">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={formErrors.email ? 'form-control border-red-500' : 'form-control'}
                          autoComplete="email"
                        />
                        {formErrors.email && (
                          <div className="field-error">
                            {formErrors.email}
                          </div>
                        )}
                      </div>
                      <div>
                        <label htmlFor="phone" className="block mb-1.5 text-sm font-medium text-black/85">
                          Телефон <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+996..."
                          className={formErrors.phone ? 'form-control border-red-500' : 'form-control'}
                          autoComplete="tel"
                        />
                        {formErrors.phone && (
                          <div className="field-error">
                            {formErrors.phone}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="instagram" className="block mb-1.5 text-sm font-medium text-black/85">
                        Instagram (опционально)
                      </label>
                      <input
                        id="instagram"
                        type="text"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleInstagramChange}
                        placeholder="@username или https://instagram.com/username"
                        className="form-control"
                        autoComplete="off"
                      />
                    </div>
                  </>

                <div className="mb-4">
                  <label htmlFor="gateway" className="block mb-1.5 text-sm font-medium text-black/85">
                    Способ оплаты <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="gateway"
                    name="gateway"
                    value={formData.gateway}
                    onChange={handleInputChange}
                    className={formErrors.gateway ? 'form-control border-red-500' : 'form-control'}
                    autoComplete="off"
                  >
                    <option value="bank_transfer">Банк. перевод</option>
                    <option value="barter">Бартер</option>
                  </select>
                  {formErrors.gateway && (
                    <div className="field-error">
                      {formErrors.gateway}
                    </div>
                  )}
                </div>

                {/* Conditional Comment Field */}
                {formData.gateway === 'barter' && (
                  <div className="mb-4">
                    <label htmlFor="barterComment" className="block mb-1.5 text-sm font-medium text-black/85">
                      Комментарий к бартеру
                    </label>
                    <textarea
                      id="barterComment"
                      name="barterComment"
                      value={formData.barterComment}
                      onChange={handleInputChange}
                      placeholder="Опишите условия бартерного обмена..."
                      className="form-control resize-y"
                      autoComplete="off"
                      rows={3}
                    />
                  </div>
                )}

                <div className="mb-4">
                  <label htmlFor="eventId" className="block mb-1.5 text-sm font-medium text-black/85">
                    Событие
                  </label>
                  <select
                    id="eventId"
                    name="eventId"
                    value={formData.eventId}
                    onChange={handleInputChange}
                    className="form-control"
                    autoComplete="off"
                  >
                    <option value="">— Активное по умолчанию —</option>
                    {events.map(ev => (
                      <option key={ev.id} value={ev.id}>
                        {ev.name}{ev.isActiveDefault ? ' (активное)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="treesCount" className="block mb-1.5 text-sm font-medium text-black/85">
                      Количество деревьев <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="treesCount"
                      type="number"
                      name="treesCount"
                      value={formData.treesCount}
                      onChange={e => handleTreesChange(e.target.value)}
                      min="1"
                      className={formErrors.treesCount ? 'form-control border-red-500' : 'form-control'}
                      autoComplete="off"
                    />
                    {formErrors.treesCount && (
                      <div className="field-error">
                        {formErrors.treesCount}
                      </div>
                    )}
                  </div>
                  <div>
                    <label htmlFor="amount" className="block mb-1.5 text-sm font-medium text-black/85">
                      Сумма (сом) <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="amount"
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={e => handleAmountChange(e.target.value)}
                      min="1"
                      className={formErrors.amount ? 'form-control border-red-500' : 'form-control'}
                      autoComplete="off"
                    />
                    {formErrors.amount && (
                      <div className="field-error">
                        {formErrors.amount}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-5 py-5 border-t border-gray-200 flex gap-3 justify-end sticky bottom-0 bg-white z-10 rounded-b-lg">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={`btn-primary ${submitting ? 'opacity-60' : ''}`}
                  disabled={submitting}
                >
                  {submitting ? 'Сохранение...' : (modalMode === 'edit' ? 'Сохранить' : 'Добавить платёж')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detailsModalOpen && selectedDonation && (
        <div className="modal-overlay" onClick={() => { setDetailsModalOpen(false); setIsEditing(false); }}>
          <div className="modal-content max-w-[600px] flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header sticky top-0 bg-white z-10 border-b border-gray-200">
              <h2 className="modal-title">Детали платежа</h2>
              <button className="modal-close" onClick={() => { setDetailsModalOpen(false); setIsEditing(false); }}>&times;</button>
            </div>
            <div className="modal-body flex-1 overflow-y-auto p-5">
              <div className="modal-field">
                <label className="modal-label">ID платежа:</label>
                <div className="modal-value">{selectedDonation.order_id}</div>
              </div>

              <div className="modal-field modal-field-stacked">
                <label className="modal-label">Донор:</label>
                <div className="modal-value">
                    <div>
                      {selectedDonation.is_partner && (selectedDonation.company_name || selectedDonation.organization_name) && (
                        <div className="mb-2 flex items-center gap-2">
                          {selectedDonation.company_logo_url && (
                            <img
                              src={selectedDonation.company_logo_url}
                              alt=""
                              className="w-6 h-6 object-contain flex-shrink-0"
                            />
                          )}
                          <span className="text-[13px] text-gray-500 font-medium">Организация:</span>
                          <span>{selectedDonation.company_name || selectedDonation.organization_name}</span>
                        </div>
                      )}
                      {(selectedDonation.first_name || selectedDonation.last_name) && (
                        <div>
                          {selectedDonation.is_partner && (
                            <span className="text-[13px] text-gray-500 font-medium">Представитель: </span>
                          )}
                          <span>{selectedDonation.first_name} {selectedDonation.last_name}</span>
                        </div>
                      )}
                    </div>
                </div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Email:</label>
                <div className="modal-value">{selectedDonation.email || '-'}</div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Телефон:</label>
                <div className="modal-value">{selectedDonation.phone || '-'}</div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Instagram:</label>
                <div className="modal-value">
                  {selectedDonation.instagram_url ? (
                    <a href={selectedDonation.instagram_url} target="_blank" rel="noopener noreferrer">
                      {selectedDonation.instagram_url}
                    </a>
                  ) : '-'}
                </div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Количество деревьев:</label>
                <div className="modal-value">{selectedDonation.trees_count}</div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Сумма:</label>
                <div className="modal-value">{formatAmount(selectedDonation.amount_kgs)}</div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Область:</label>
                <div className="modal-value">
                  {selectedDonation.region_id
                    ? (selectedDonation.region_name?.ru || selectedDonation.region_name?.en || selectedDonation.region_code || '—')
                    : <span className="text-gray-400">Кыргызстан</span>}
                </div>
              </div>
              <div className="modal-field">
                <label className="modal-label">Способ оплаты:</label>
                <div className="modal-value">{getGatewayLabel(selectedDonation.gateway)}</div>
              </div>
              {selectedDonation.gateway === 'barter' && (
                <div className="modal-field modal-field-stacked">
                  <label className="modal-label">Комментарий:</label>
                  <div className="modal-value">
                    {selectedDonation.comment ? (
                      <div className="mt-1 p-3 bg-[#f5f5f5] rounded border border-[#e0e0e0]">
                        {selectedDonation.comment}
                      </div>
                    ) : (
                      'Без комментария'
                    )}
                  </div>
                </div>
              )}
              <div className="modal-field">
                <label className="modal-label">Статус:</label>
                <div className="modal-value">
                  <span
                    className="font-medium"
                    // dynamic: color derived from status
                    style={{ color: getStatusColor(selectedDonation.status) }}
                  >
                    {getStatusLabel(selectedDonation.status)}
                  </span>
                </div>
              </div>
              {selectedDonation.certificate_number && (
                <div className="modal-field">
                  <label className="modal-label">Сертификат:</label>
                  <div className="modal-value">
                    <div className="relative">
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          setCertificatePopup(selectedDonation.certificate_number!)
                        }}
                        className="text-[#1890ff] cursor-pointer underline"
                      >
                        {selectedDonation.certificate_number}
                      </span>
                      {selectedDonation.certificate_sent_at && (
                        <span className="ml-2 text-[#52c41a]">✅ Отправлен</span>
                      )}
                      {certificatePopup === selectedDonation.certificate_number && (
                        <>
                          <div
                            className="fixed inset-0 z-[9999]"
                            onClick={(e) => {
                              e.stopPropagation()
                              setCertificatePopup(null)
                            }}
                          />
                          <div
                            className="absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg z-[10000] min-w-[180px] overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <a
                              href={`${API_URL}/api/certificate/${selectedDonation.certificate_number}/jpeg`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-2.5 text-[#333] no-underline text-sm border-b border-[#f0f0f0] transition-colors hover:bg-[#f5f5f5]"
                            >
                              Скачать JPEG
                            </a>
                            <a
                              href={`${API_URL}/api/certificate/${selectedDonation.certificate_number}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-2.5 text-[#333] no-underline text-sm transition-colors hover:bg-[#f5f5f5]"
                            >
                              Скачать PDF файл
                            </a>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {selectedDonation.ticket_number && (
                <div className="modal-field">
                  <label className="modal-label">Билет:</label>
                  <div className="modal-value">
                    <a
                      href={`${API_URL}/api/tickets/${selectedDonation.ticket_number}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1890ff] cursor-pointer underline"
                    >
                      {selectedDonation.ticket_number}
                    </a>
                    {selectedDonation.participants_count && (
                      <span className="ml-2 text-gray-500">
                        ({selectedDonation.participants_count} участник{selectedDonation.participants_count === 1 ? '' : selectedDonation.participants_count < 5 ? 'а' : 'ов'})
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="modal-field">
                <label className="modal-label">Дата создания:</label>
                <div className="modal-value">{formatDate(selectedDonation.created_at)}</div>
              </div>
              {selectedDonation.paid_at && (
                <div className="modal-field">
                  <label className="modal-label">Дата оплаты:</label>
                  <div className="modal-value">{formatDate(selectedDonation.paid_at)}</div>
                </div>
              )}
            </div>
            <div className="modal-footer sticky bottom-0 bg-white z-10 border-t border-gray-200 flex gap-3 justify-between px-5 py-4 flex-wrap">
              <button
                className="btn-secondary"
                onClick={() => selectedDonation && openRepeatModal(selectedDonation)}
                disabled={!selectedDonation}
                title="Создать новый платеж с теми же данными"
              >
                Повторить платеж
              </button>
              <div className="flex gap-3">
                <button className="btn-secondary" onClick={() => { setDetailsModalOpen(false); setIsEditing(false); }}>
                  Закрыть
                </button>
                <button
                  className="btn-primary"
                  onClick={() => selectedDonation && openEditModal(selectedDonation)}
                  disabled={!selectedDonation}
                >
                  Редактировать
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Попап выбора партнёра (из «Добавить платёж» и «Редактировать») */}
      {companyPickerOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]"
          onMouseDown={(e) => {
            // Клик по overlay закрывает попап, но НЕ трогает родительскую модалку
            if (e.target === e.currentTarget) {
              e.stopPropagation()
              setCompanyPickerOpen(null)
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-white rounded-lg w-[90%] max-w-[440px] max-h-[80vh] shadow-md flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold m-0">Выбрать партнёра</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setCompanyPickerOpen(null)
                }}
                className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-3 border-b border-gray-100">
              <input
                type="text"
                value={companyPickerSearch}
                onChange={e => setCompanyPickerSearch(e.target.value)}
                placeholder="Поиск…"
                className="form-control"
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {companies
                .filter(c => c.name.toLowerCase().includes(companyPickerSearch.toLowerCase()))
                .map(c => {
                  const isSelected = String(c.id) === companyPickerTempId
                  return (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 px-5 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <input
                        type="radio"
                        name="companyPicker"
                        checked={isSelected}
                        onChange={() => setCompanyPickerTempId(String(c.id))}
                        className="w-4 h-4 flex-shrink-0"
                      />
                      {c.logo_url ? (
                        <img src={c.logo_url} alt="" className="w-9 h-9 object-contain flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 bg-gray-100 rounded flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate">{c.name}</span>
                    </label>
                  )
                })}
              {companies.filter(c => c.name.toLowerCase().includes(companyPickerSearch.toLowerCase())).length === 0 && (
                <div className="px-5 py-4 text-sm text-gray-500 text-center">Не найдено</div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex justify-between gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setCompanyPickerOpen(null)
                }}
                className="btn-secondary"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  const selectedCo = companies.find(x => String(x.id) === companyPickerTempId)
                  if (companyPickerOpen === 'add') {
                    setFormData(p => ({
                      ...p,
                      companyId: companyPickerTempId,
                      organizationName: selectedCo?.name || ''
                    }))
                    if (formErrors.companyId) setFormErrors(p => ({ ...p, companyId: '' }))
                  } else if (companyPickerOpen === 'edit') {
                    setEditedCompanyId(companyPickerTempId)
                    setEditedOrganizationName(selectedCo?.name || '')
                  }
                  setCompanyPickerOpen(null)
                }}
                className="btn-primary"
                disabled={!companyPickerTempId}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
